/**
 * Integrated API Service - Real Backend Integration
 * Connects to AWS API Gateway with Cognito authentication
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://2jhc5i9ex2.execute-api.ap-southeast-1.amazonaws.com/prod';

// ============================================================================
// TYPES
// ============================================================================

export interface Analysis {
  analysisId: string;
  userId: string;
  repositoryUrl: string;
  repositoryName: string;
  status: 'initiated' | 'processing' | 'completed' | 'failed';
  workflowState: string;
  stages: {
    project_review: StageStatus;
    intelligence_report: StageStatus;
    interview_simulation: StageStatus;
  };
  cost: {
    totalCostUsd: number;
    breakdown: {
      repoProcessing: number;
      stage1: number;
      stage2: number;
      stage3: number;
    };
  };
  createdAt: string;
  updatedAt: string;
  errorMessage?: string;
}

export interface StageStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  errorMessage?: string;
}

export interface AnalysisStatus {
  analysisId: string;
  status: string;
  workflowState: string;
  progress: number;
  stages: {
    project_review: {
      status: string;
      progress: number;
      startedAt?: string;
      completedAt?: string;
    };
    intelligence_report: {
      status: string;
      progress: number;
      startedAt?: string;
      completedAt?: string;
    };
    interview_simulation: {
      status: string;
      progress: number;
      startedAt?: string;
      completedAt?: string;
    };
  };
  errorMessage?: string;
}

export interface ProjectReview {
  codeQuality: {
    overall: number;
    readability: number;
    maintainability: number;
    testCoverage: number;
    documentation: number;
    errorHandling: number;
    security: number;
    performance: number;
    justification: string;
  };
  architectureClarity: {
    score: number;
    componentOrganization: string;
    separationOfConcerns: string;
    designPatterns: string[];
    antiPatterns: string[];
  };
  employabilitySignal: {
    overall: number;
    productionReadiness: number;
    professionalStandards: number;
    complexity: string;
    companyTierMatch: {
      bigTech: number;
      productCompanies: number;
      startups: number;
      serviceCompanies: number;
    };
    justification: string;
  };
  strengths: Array<{
    strengthId: string;
    pattern: string;
    description: string;
    impact: string;
    fileReferences: Array<{ file: string }>;
  }>;
  weaknesses: Array<{
    weaknessId: string;
    issue: string;
    severity: string;
    impact: string;
    fileReferences: Array<{ file: string }>;
  }>;
  criticalIssues: any[];
  projectAuthenticity: {
    score: number;
    confidence: string;
    signals: {
      commitDiversity: number;
      timeSpread: number;
      messageQuality: number;
      codeEvolution: number;
    };
    warnings: string[];
  };
}

export interface IntelligenceReport {
  systemArchitecture?: {
    overview: string;
    layers: Array<{
      name: string;
      components: string[];
      responsibilities: string[];
      fileReferences: Array<{ file: string }>;
    }>;
    componentDiagram: string;
    dataFlowDiagram: string;
    technologyStack: {
      languages: Record<string, number>;
      frameworks: string[];
      databases: string[];
      devTools: string[];
      infrastructure?: string[];
    };
  };
  designDecisions: Array<{
    decisionId: string;
    decision: string;
    rationale: string;
    tradeoffs: string;
    alternatives: string;
    fileReferences: Array<{ file: string }>;
  }>;
  technicalInsights: Array<{
    insightId: string;
    insight: string;
    significance: string;
    fileReferences: Array<{ file: string }>;
  }>;
  architecturePatterns: string[];
  technologyStack: {
    languages: string[];
    frameworks: string[];
    libraries: string[];
    tools: string[];
  };
}

export interface InterviewQuestion {
  questionId: string;
  questionNumber: number;
  category: string;
  difficulty: string;
  type?: string;
  question: string;
  expectedTopics: string[];
  groundedIn: Array<{ file: string; reason: string }>;
  hints: string[];
  followUpQuestions: string[];
}

export interface InterviewTopic {
  topicId: string;
  title: string;
  category: string;
  description: string;
  evaluationSignals: string[];
  fulfillmentThreshold: number;
  maxFollowUps: number;
  difficulty: string;
  currentFulfillment: number;
  followUpsAsked: number;
  isCompleted: boolean;
}

export interface PerformanceSignal {
  signalId: string;
  name: string;
  category: string;
  score: number;
  evidence: string[];
  confidence: number;
}

export interface InterviewPlan {
  analysisId: string;
  candidateLevel: string;
  targetRole: string;
  phases: {
    warmup: string[];
    deep_dive: string[];
    stretch: string[];
  };
  allTopics: Record<string, InterviewTopic>;
  requiredSignals: string[];
  generatedAt: string;
}

export interface InterviewSimulation {
  questions: InterviewQuestion[];
  totalQuestions: number;
  categoryDistribution: {
    architecture: number;
    implementation: number;
    tradeoffs: number;
    scalability: number;
    debugging: number;
  };
  difficultyDistribution: {
    easy: number;
    medium: number;
    hard: number;
  };
  mode?: 'sheet' | 'live';
  completedModes?: {
    sheet: boolean;
    live: boolean;
  };
}

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  targetRole: string;
  language: string;
  subscription: {
    tier: string;
    analysisQuota: number;
    analysisUsed: number;
    resetAt: string;
  };
  preferences: {
    emailNotifications: boolean;
    emailDigest: boolean;
  };
  createdAt: string;
  lastLoginAt: string;
}

export interface UserStats {
  totalAnalyses: number;
  completedAnalyses: number;
  averageCodeQuality: number;
  totalInterviewSessions: number;
  averageInterviewScore: number;
  lastAnalysisDate: string | null;
  lastInterviewDate: string | null;
}

export interface UserProgress {
  improvementTrend: Array<{
    date: string;
    metric: string;
    value: number;
  }>;
  identifiedSkillGaps: Array<{
    skill: string;
    currentLevel: number;
    targetLevel: number;
    priority: 'high' | 'medium' | 'low';
    learningResources: string[];
  }>;
  recommendedTopics: string[];
  completedTopics: string[];
  categoryPerformance: {
    architecture: { averageScore: number; trend: 'improving' | 'stable' | 'declining' };
    implementation: { averageScore: number; trend: 'improving' | 'stable' | 'declining' };
    tradeoffs: { averageScore: number; trend: 'improving' | 'stable' | 'declining' };
    scalability: { averageScore: number; trend: 'improving' | 'stable' | 'declining' };
  };
}

export interface InterviewSession {
  sessionId: string;
  userId: string;
  analysisId: string;
  status: 'active' | 'completed' | 'abandoned';
  config: {
    targetRole?: string;
    difficulty?: string;
    timeLimit?: number;
    feedbackMode?: string;
    questionCount?: number;
    questionTypes?: string[];
    intensity?: 'fast' | 'normal' | 'deep';
  };
  interviewPlan?: InterviewPlan;
  createdAt: string;
  ttl?: number;
  progress: {
    questionsAnswered: number;
    questionsSkipped: number;
    averageScore: number;
    totalTimeSpentSeconds: number;
    activeTopicId?: string;
    currentPhase?: 'warmup' | 'deep_dive' | 'stretch' | 'completed';
    currentQuestionOverride?: string;
    signals?: Record<string, PerformanceSignal>;
    topicState?: Record<string, {
      currentFulfillment: number;
      isCompleted: boolean;
      followUpsAsked: number;
    }>;
  };
  totalQuestions: number;
  currentQuestionIndex: number;
  startedAt: string;
  completedAt?: string;
  questions: InterviewQuestion[];
  answers: Array<{
    questionId: string;
    answer: string;
    submittedAt?: string;
    timeSpentSeconds?: number;
    evaluation?: AnswerEvaluation;
  }>;
  summary?: InterviewSummary;
}

export interface InterviewSummary {
  overallScore: number;
  totalTimeMinutes: number;
  questionsAnswered: number;
  categoryPerformance: {
    technical: number;
    behavioral: number;
    'system-design': number;
  };
  topStrengths: string[];
  areasForImprovement: string[];
  recommendations: string[];
  comparedToTarget: {
    role: string;
    readiness: 'not_ready' | 'needs_work' | 'almost_ready' | 'ready';
    percentile: number;
  };
}

export interface AnswerEvaluation {
  overallScore: number;
  criteriaScores: {
    technicalAccuracy: number;
    completeness: number;
    clarity: number;
    depthOfUnderstanding: number;
  };
  strengths: string[];
  weaknesses: string[];
  missingKeyPoints: string[]; /* legacy */
  keyPointsCoverage?: {
    covered: string[];
    partiallyCovered: string[];
    missed: string[];
  };
  comparison: {
    weakAnswer: string;
    strongAnswer: string;
    yourAnswerCategory: 'weak' | 'acceptable' | 'strong' | 'excellent';
  };
  feedback: string; /* legacy */
  detailedFeedback?: string;
  improvementSuggestions: string[];
}

export interface FileInfo {
  path: string;
  tier: number;
  priority: number;
  category: string;
  selected: boolean;
  rank: number;
  inTop30: boolean;
  language: string;
  size: string;
}

export interface FileSelection {
  analysisId: string;
  totalFiles: number;
  selectedFiles: number;
  top30Files: string[];
  files: FileInfo[];
  filters: {
    languages: string[];
    frameworks: string[];
    tiers: string[];
  };
}

// ============================================================================
// AUTHENTICATION HELPER
// ============================================================================

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token || null;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

// ============================================================================
// API HELPER
// ============================================================================

async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  requiresAuth: boolean = true
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add authorization header if required
  if (requiresAuth) {
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}`,
    }));
    throw new Error(error.error || error.message || `Request failed with status ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// ANALYSIS APIs
// ============================================================================

export const startAnalysis = (repositoryUrl: string) =>
  apiCall<{ analysisId: string; status: string; estimatedCompletionTime: number }>('/analyze', {
    method: 'POST',
    body: JSON.stringify({ repositoryUrl }),
  });

export const getAnalysisStatus = (analysisId: string) =>
  apiCall<AnalysisStatus>(`/analysis/${analysisId}/status`);

export const getAnalysis = (analysisId: string) =>
  apiCall<{
    analysis: Analysis;
    repository: any;
    projectReview?: ProjectReview;
    intelligenceReport?: IntelligenceReport;
    interviewSimulation?: InterviewSimulation;
  }>(`/analysis/${analysisId}`);

export const getAnalyses = (limit: number = 20, cursor?: string) =>
  apiCall<{
    items: Analysis[];
    nextCursor?: string;
    hasMore: boolean;
  }>(`/analyses?limit=${limit}${cursor ? `&cursor=${cursor}` : ''}`);

export const deleteAnalysis = (analysisId: string) =>
  apiCall<void>(`/analysis/${analysisId}`, {
    method: 'DELETE',
  });

export const getAnalysisEvents = (analysisId: string) =>
  apiCall<{ events: any[] }>(`/analysis/${analysisId}/events`);

export const getAnalysisCost = (analysisId: string) =>
  apiCall<{
    totalCostUsd: number;
    breakdown: any;
  }>(`/analysis/${analysisId}/cost`);

// ============================================================================
// PROGRESSIVE WORKFLOW APIs
// ============================================================================

export const continueToStage2 = (analysisId: string) =>
  apiCall<{
    analysisId: string;
    message: string;
    status: string;
    estimatedCompletionTime: number;
  }>(`/analysis/${analysisId}/continue-stage2`, {
    method: 'POST',
  });

export const continueToStage3 = (analysisId: string, mode: 'sheet' | 'live' = 'sheet', targetRole?: string, candidateLevel?: string) =>
  apiCall<{
    analysisId: string;
    message: string;
    status: string;
    estimatedCompletionTime: number;
  }>(`/analysis/${analysisId}/continue-stage3`, {
    method: 'POST',
    body: JSON.stringify({ mode, targetRole, candidateLevel }),
  });

export const cancelAnalysis = (analysisId: string) =>
  apiCall<{ success: boolean; message: string }>(`/analysis/${analysisId}/cancel`, {
    method: 'POST',
  });


// ============================================================================
// FILE MANAGEMENT APIs
// ============================================================================

export const getAnalysisFiles = (analysisId: string) =>
  apiCall<FileSelection>(`/analysis/${analysisId}/files`);

export const updateFileSelection = (
  analysisId: string,
  data: {
    selectedFiles?: string[];
    deselectedFiles?: string[];
  }
) =>
  apiCall<{
    analysisId: string;
    selectedFiles: number;
    deselectedFiles: number;
    message: string;
  }>(`/analysis/${analysisId}/files/selection`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const reorderFiles = (analysisId: string, customOrder: string[]) =>
  apiCall<{
    analysisId: string;
    customOrder: number;
    message: string;
  }>(`/analysis/${analysisId}/files/reorder`, {
    method: 'POST',
    body: JSON.stringify({ customOrder }),
  });

export const reprocessAnalysis = (analysisId: string) =>
  apiCall<{
    analysisId: string;
    status: string;
    message: string;
    selectedFiles: number;
  }>(`/analysis/${analysisId}/reprocess`, {
    method: 'POST',
  });

// ============================================================================
// USER APIs
// ============================================================================

export const getUserProfile = () =>
  apiCall<UserProfile>('/user/profile');

export const createUserProfile = (data: {
  displayName: string;
  targetRole: string;
  language: string;
}) =>
  apiCall<UserProfile>('/user/profile', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateUserPreferences = (data: {
  targetRole?: string;
  language?: string;
  emailNotifications?: boolean;
  emailDigest?: boolean;
}) =>
  apiCall<UserProfile>('/user/preferences', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const getUserStats = () =>
  apiCall<UserStats>('/user/stats');

export const getUserProgress = () =>
  apiCall<UserProgress>('/user/progress');

// ============================================================================
// INTERVIEW APIs
// ============================================================================

export const createInterviewSession = (data: {
  analysisId: string;
  config?: {
    targetRole?: string;
    difficulty?: string;
    timeLimit?: number;
    feedbackMode?: string;
    questionCount?: number;
    questionTypes?: string[];
    intensity?: 'fast' | 'normal' | 'deep';
  };
}) =>
  apiCall<InterviewSession>('/interview/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getInterviewSession = (sessionId: string) =>
  apiCall<InterviewSession>(`/interview/sessions/${sessionId}`);

export const getInterviewSessions = (analysisId?: string) =>
  apiCall<InterviewSession[]>(`/interview/sessions${analysisId ? `?analysisId=${analysisId}` : ''}`);

export const getSessionAttempts = (sessionId: string) =>
  apiCall<any[]>(`/interview/sessions/${sessionId}/attempts`);

export const submitAnswer = (
  sessionId: string,
  data: {
    questionId: string;
    questionText?: string;
    answer: string;
    timeSpentSeconds: number;
    action?: 'submit' | 'skip_question' | 'end_early';
  }
) =>
  apiCall<{
    attemptId: string;
    questionId: string;
    evaluation: AnswerEvaluation;
    improvementFromPrevious: number;
    followUpQuestions?: InterviewQuestion[];
  }>(`/interview/sessions/${sessionId}/answer`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getFollowUp = (
  sessionId: string,
  data: {
    analysisId: string;
    questionId: string;
    questionText: string;
    answer: string;
    evaluation: any;
    coverageMap?: Record<string, boolean>;
    interviewContext?: any;
  }
) =>
  apiCall<{
    success: boolean;
    followUpQuestions: InterviewQuestion[];
    reason?: string;
  }>(`/interview/sessions/${sessionId}/followup`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const completeInterviewSession = (sessionId: string) =>
  apiCall<InterviewSession>(`/interview/sessions/${sessionId}/complete`, {
    method: 'POST',
  });

// ============================================================================
// POLLING UTILITIES
// ============================================================================

export async function pollAnalysisStatus(
  analysisId: string,
  onProgress?: (status: AnalysisStatus) => void,
  maxAttempts: number = 120, // 10 minutes max (5 second intervals)
  interval: number = 5000
): Promise<Analysis> {
  let attempts = 0;

  while (attempts < maxAttempts) {
    const status = await getAnalysisStatus(analysisId);

    if (onProgress) {
      onProgress(status);
    }

    if (status.status === 'completed') {
      const fullAnalysis = await getAnalysis(analysisId);
      return fullAnalysis.analysis;
    }

    if (status.status === 'failed') {
      throw new Error(status.errorMessage || 'Analysis failed');
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, interval));
    attempts++;
  }

  throw new Error('Analysis timeout - taking longer than expected');
}

// ============================================================================
// EXPORT FUNCTIONALITY
// ============================================================================

export async function exportAnalysis(
  analysisId: string,
  format: 'pdf' | 'json' | 'markdown'
): Promise<{ downloadUrl: string; filename: string }> {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE}/analysis/${analysisId}/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ format }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Export failed');
  }

  return response.json();
}

// ============================================================================
// COST TRACKING API
// ============================================================================

/**
 * Get real-time cost metrics
 */
export const getCostRealtime = () =>
  apiCall<any>('/cost/realtime', {}, true);

/**
 * Get cost breakdown for specific analysis
 */
export const getCostAnalysis = (analysisId: string) =>
  apiCall<any>(`/cost/analysis/${analysisId}`, {}, true);

/**
 * Get daily cost summary
 */
export const getCostDaily = (date: string) =>
  apiCall<any>(`/cost/daily/${date}`, {}, true);

/**
 * Get cost range between dates
 */
export const getCostRange = (startDate: string, endDate: string) =>
  apiCall<any>(`/cost/range?start=${startDate}&end=${endDate}`, {}, true);

/**
 * Get monthly cost projection
 */
export const getCostProjection = () =>
  apiCall<any>('/cost/projection', {}, true);

/**
 * Get cost comparison by model
 */
export const getCostModels = (days: number = 7) =>
  apiCall<any>(`/cost/models?days=${days}`, {}, true);

/**
 * Export cost data as CSV
 */
export async function exportCostData(format: 'csv' | 'json' = 'csv', days: number = 30): Promise<string> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/cost/export?format=${format}&days=${days}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  return response.text();
}

/**
 * Get current model pricing
 */
export const getModelPricing = () =>
  apiCall<any>('/cost/pricing', {}, true);

// ============================================================================
// EXPORT DEFAULT API OBJECT
// ============================================================================

const api = {
  // Analysis
  startAnalysis,
  getAnalysisStatus,
  getAnalysis,
  getAnalyses,
  deleteAnalysis,
  getAnalysisEvents,
  getAnalysisCost,
  pollAnalysisStatus,
  exportAnalysis,

  // Progressive Workflow
  continueToStage2,
  continueToStage3,

  // File Management
  getAnalysisFiles,
  updateFileSelection,
  reorderFiles,
  reprocessAnalysis,

  // User
  getUserProfile,
  createUserProfile,
  updateUserPreferences,
  getUserStats,
  getUserProgress,

  // Interview
  createInterviewSession,
  getInterviewSession,
  submitAnswer,
  completeInterviewSession,

  // Cost Analytics (Admin)
  getCostRealtime,
  getCostRange,
  getCostModels,
  getCostProjection,
  getCostDaily,
  getCostAnalysis,
  exportCostData,
  getModelPricing,
};

export default api;
